import { Combobox, ComboboxButton, Field, Label } from '@headlessui/react';
import { FC, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, CrossIcon, SearchIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Checkbox } from '../../../components/checkbox/index';
import { ComboboxOptions } from '../../../components/combobox/index';
import Input from '../../../components/input/input';
import { useDebounce } from '../../../hooks/use-debounce.hook';
import {
  companyIndustryMap,
  companyIndustryTranslation,
} from '../../../translations/company-translation';
import { CompanyIndustry } from '../../../types/company.types';

type ItemProps = {
  checked: boolean;
  data: CompanyIndustry;
  onChange: () => void;
};

const Item: FC<ItemProps> = ({ data, checked, onChange }) => {
  return (
    <Field>
      <div
        className={twMerge(
          'relative flex w-full cursor-pointer items-center gap-3 rounded-[4px] px-[6px] py-2 hover:bg-gray-50',
          checked && 'hover:bg-bg-brand-25 bg-brand-25',
        )}
      >
        <Checkbox checked={checked} id={`select-industry-${data}`} onChange={onChange} />

        <span className="max-w-[200px] truncate text-sm text-gray-700">
          <AppFormattedMessage id={companyIndustryTranslation[data]} />
        </span>

        <Label
          className="absolute left-0 top-0 h-full w-full cursor-pointer"
          htmlFor={`select-industry-${data}`}
        />
      </div>
    </Field>
  );
};

type Props = {
  setValue: (value: CompanyIndustry[]) => void;
  value: CompanyIndustry[];
};

export const IndustrySelectCombobox: FC<Props> = ({ setValue, value: selectedIndustries }) => {
  const selectedSet = new Set(selectedIndustries);

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [search, setSearch] = useState('');

  const { debounceCallback } = useDebounce<string>({ callback: setDebouncedSearch });

  const handleSearch = (value: string) => {
    debounceCallback(value);
    setSearch(value);
  };

  const industries = useMemo(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    const industryList = companyIndustryMap as CompanyIndustry[];

    if (!normalizedSearch) return industryList;

    return industryList.filter((industry) =>
      companyIndustryTranslation[industry].toLowerCase().includes(normalizedSearch),
    );
  }, [debouncedSearch]);

  const toggleIndustry = (industry: CompanyIndustry) => {
    const exists = selectedIndustries.includes(industry);

    const nextValue = exists
      ? selectedIndustries.filter((i) => i !== industry)
      : [...selectedIndustries, industry];

    setValue(nextValue);
  };

  return (
    <Combobox as="div" className="relative">
      <div
        className={twMerge(
          'flex h-9 w-full items-center justify-between gap-2 rounded border border-gray-100 bg-gray-25 px-3 py-2 text-sm font-[450] text-gray-700',
        )}
      >
        {selectedIndustries.length === 0 ? (
          <span>
            <AppFormattedMessage id="PERKS_ALL_INDUSTRIES" />
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {selectedIndustries.slice(0, 1).map((industry) => (
              <div
                className="flex w-fit items-center gap-3 rounded-3xl bg-brand-50 px-3"
                key={`view-${industry}`}
              >
                <span className="max-w-[220px] truncate text-label-md text-gray-700">
                  <AppFormattedMessage id={companyIndustryTranslation[industry]} />
                </span>
              </div>
            ))}
            {selectedIndustries?.length > 1 && (
              <span className="text-label-md text-gray-700">
                <AppFormattedMessage
                  id="PERKS_INDUSTRY_MORE_COUNT"
                  values={{ count: selectedIndustries.length - 1 }}
                />
              </span>
            )}
          </div>
        )}
        <ChevronDownIcon />
        <ComboboxButton className="absolute left-0 top-0 z-1 h-full w-full" />
      </div>

      <ComboboxOptions className="left-auto right-0 max-h-[450px] w-full min-w-[330px]">
        <Input
          icon={<SearchIcon iconColor="#98A2B3" />}
          onChange={handleSearch}
          placeholder={<AppFormattedMessage id="SEARCH" />}
          value={search}
          wrapperClassName=" w-full"
        />

        {selectedIndustries.length !== 0 && (
          <div className="flex h-fit max-h-[120px] flex-shrink-0 flex-wrap items-start gap-2 overflow-y-auto">
            {selectedIndustries?.map((industry) => (
              <div
                className="flex w-fit items-center gap-3 rounded-3xl bg-brand-50 p-[6px] px-3"
                key={`preview-${industry}`}
                onClick={() => toggleIndustry(industry)}
              >
                <span className="max-w-[220px] truncate text-sm text-gray-700">
                  <AppFormattedMessage id={companyIndustryTranslation[industry]} />
                </span>
                <CrossIcon className="size-[14px] cursor-pointer" />
              </div>
            ))}
          </div>
        )}
        <div className="flex h-fit w-full flex-col items-stretch justify-start gap-2 overflow-y-auto">
          {industries.map((industry) => {
            return (
              <Item
                checked={selectedSet.has(industry)}
                data={industry}
                key={industry}
                onChange={() => toggleIndustry(industry)}
              />
            );
          })}
        </div>
      </ComboboxOptions>
    </Combobox>
  );
};
